import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import EncoderAPI from "../api/EncoderAPI";
import StatusTag from "../components/encoding/StatusTag";
import { WebSocketContext } from "../contexts/WebSocket";

import type { AppState } from "../reducers";
import type { EncodingProgressEvent } from "./useEncodingWS";
import type { EncodingStatus, EncodingType } from "../api/EncoderAPI.types";

export interface EncodingJobTableItem {
  uuid: string;
  mediaUuid: string;
  mediaTitle: string;
  progression: number | null;
  status: EncodingStatus;
  type: EncodingType;
}

function useInProgressEncodingList(): {
  isLoading: boolean;
  itemList: EncodingJobTableItem[];
  totalItems: number;
} {
  const [itemList, setItemList] = useState<EncodingJobTableItem[]>([]);

  const { joinChannel, channels } = useContext(WebSocketContext);

  const { t } = useTranslation("navbar");

  const organizationUuid = useSelector(
    (state: AppState) => state.fetchReducer.orga_config.data.organization_uuid,
  );

  const userUuid = useSelector(
    (state: AppState) => state.fetchReducer.user.data.uuid,
  );

  const channelTopic = `editor:${organizationUuid}:${userUuid}`;

  const channel = useMemo(
    () => channels?.[channelTopic] ?? null,
    [channels, channelTopic],
  );

  const showEncodingToast = useCallback(
    (
      variant: "error" | "success",
      message: string,
      encodingType: EncodingType,
      duration: number,
    ): void => {
      let toastId: string | number = "";
      const action = (
        <div className="flex gap-8">
          <StatusTag>{t(`editor-encoding:${encodingType}`)}</StatusTag>
          <button
            type="button"
            onClick={() => {
              toast.dismiss(toastId);
            }}
          >
            <Icon path={mdiClose} size={0.625} />
          </button>
        </div>
      );

      if (variant === "error") {
        toastId = toast.error(message, {
          action,
          duration,
          position: "top-right",
          className: "whitespace-nowrap",
        });
        return;
      }

      toastId = toast.success(message, {
        action,
        duration,
        position: "top-right",
        className: "whitespace-nowrap",
      });
    },
    [t],
  );

  const handleNotificationEventMessage = useCallback(
    (message: EncodingProgressEvent): void => {
      const isProgressEvent =
        message.event === "progress" && message.subject === "encoding";

      const isFailedEvent =
        message.event === "failed" && message.subject === "encoding";

      const isSuccessEvent =
        message.event === "done" && message.subject === "encoding";

      const isNewEvent =
        message.event === "new" && message.subject === "encoding";

      if (isProgressEvent) {
        setItemList((prevList) =>
          prevList.map((item) => {
            if (item.uuid === message.data.encoding_job_uuid) {
              return {
                ...item,
                progression: message.data.progression,
              };
            }
            return item;
          }),
        );
      }

      if (isFailedEvent) {
        setItemList((prevList) =>
          prevList.filter(
            (item) => item.uuid !== message.data.encoding_job_uuid,
          ),
        );
        if (message.data.encoding_job_user_uuid === userUuid) {
          showEncodingToast(
            "error",
            t("encodingJobError", {
              mediaLabel: message.data.media_label,
            }),
            message.data.type,
            Infinity,
          );
        }
      }

      if (isSuccessEvent) {
        setItemList((prevList) =>
          prevList.filter(
            (item) => item.uuid !== message.data.encoding_job_uuid,
          ),
        );
        if (message.data.encoding_job_user_uuid === userUuid) {
          showEncodingToast(
            "success",
            t("encodingJobSuccess", {
              mediaLabel: message.data.media_label,
            }),
            message.data.type,
            5000,
          );
        }
      }

      if (isNewEvent) {
        const newItem: EncodingJobTableItem = {
          uuid: message.data.encoding_job_uuid,
          mediaUuid: message.data.media_uuid,
          mediaTitle: message.data.media_label,
          progression: message.data.progression,
          status: message.data.status,
          type: message.data.type,
        };
        setItemList((prevList) => [newItem, ...prevList]);
      }
    },
    [userUuid, t, showEncodingToast],
  );

  const getEncodingListQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingList,
    params: [
      {
        page: 1,
        perPage: 25,
        filters: {
          status: "transcoding",
        },
        sort: {
          field: "progression",
          direction: "up",
        },
      },
    ],
  });

  const { isLoading, data: encodingListResponse } =
    useQuery(getEncodingListQuery);

  useEffect(() => {
    if (organizationUuid && userUuid) {
      joinChannel({
        channelTopic,
      });
    }
  }, [joinChannel, channelTopic, organizationUuid, userUuid]);

  useEffect(() => {
    if (!channel) return (): void => {};

    const notificationRef = channel.on(
      "notification",
      handleNotificationEventMessage,
    );

    return (): void => {
      channel.off("notification", notificationRef);
    };
  }, [channel, handleNotificationEventMessage]);

  useEffect(() => {
    const encodingJobTableItemList: EncodingJobTableItem[] =
      encodingListResponse?.data.map((encodingJob) => ({
        uuid: encodingJob.uuid,
        mediaUuid: encodingJob.media_uuid,
        mediaTitle: encodingJob.media.label,
        progression: encodingJob.progression,
        status: encodingJob.status,
        type: encodingJob.type,
      })) ?? [];

    setItemList(encodingJobTableItemList);
  }, [encodingListResponse?.data]);

  return {
    isLoading,
    itemList,
    totalItems: itemList.length,
  };
}

export default useInProgressEncodingList;
